import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DollarSign, Loader2, AlertCircle, Calculator, Info, ChevronDown, ChevronRight } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/contexts/AuthContext";
import { useProgramYear } from "@/contexts/ProgramYearContext";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { paymentAdjustmentCache, fetchPaymentAdjustmentConfigs } from "@/lib/admin-cache";

interface PaymentAdjustmentConfigData {
  id: number;
  performance_year: number;
  payment_year: number;
  low_scenario_input: number;
  medium_scenario_calc: number;
  high_scenario_calc: number;
  scaling_factor: number;
  low_actual_max: number;
  medium_actual_max: number;
  high_actual_max: number;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
const SCALING_FACTOR = 0.74;

export function PaymentAdjustmentConfig() {
  const { toast } = useToast();
  const { user, token } = useAuth();
  const { programYear } = useProgramYear();
  // Stale-while-revalidate: seed from the login-time prefetch cache so the
  // card renders instantly; the effect below still refreshes in background.
  const cachedConfig =
    (paymentAdjustmentCache.configs as PaymentAdjustmentConfigData[]).find(
      c => c.performance_year === programYear
    ) || null;
  const [config, setConfig] = useState<PaymentAdjustmentConfigData | null>(cachedConfig);
  const [loading, setLoading] = useState(!paymentAdjustmentCache.loaded);
  const [saving, setSaving] = useState(false);
  const [lowScenarioInput, setLowScenarioInput] = useState<string>(
    cachedConfig ? cachedConfig.low_scenario_input.toString() : '2.5'
  );
  const [helpOpen, setHelpOpen] = useState(false);

  const isAdmin = user?.role === 'admin';
  const paymentYear = programYear + 2;

  // Auto-calculated scenario maximums
  const lowInput = parseFloat(lowScenarioInput) || 0;
  const mediumCalc = lowInput * 1.25;
  const highCalc = lowInput * 1.40;
  const validInput = lowInput >= 0.5 && lowInput <= 10.0;

  // Fetch the config for the viewing year whenever the year changes
  useEffect(() => {
    const fetchConfig = async () => {
      if (!token) return;
      if (!paymentAdjustmentCache.loaded) setLoading(true);
      try {
        const data: PaymentAdjustmentConfigData[] =
          await fetchPaymentAdjustmentConfigs(token);
        const found = data.find(c => c.performance_year === programYear) || null;
        // This usually runs as a BACKGROUND refresh after a cache-seeded
        // render. Only touch state when the server value actually differs —
        // unconditionally resetting caused a visible jump on every tab visit
        // and clobbered anything the admin had already typed in the input.
        const unchanged =
          (config?.id ?? null) === (found?.id ?? null) &&
          (config?.low_scenario_input ?? null) === (found?.low_scenario_input ?? null);
        if (!unchanged) {
          setConfig(found);
          setLowScenarioInput(found ? found.low_scenario_input.toString() : '2.5');
        }
      } catch (error) {
        console.error('Error fetching payment adjustment config:', error);
        toast({
          title: "Error",
          description: "Failed to load payment adjustment configuration.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, [token, programYear, toast]);

  const handleSave = async () => {
    const lowValue = parseFloat(lowScenarioInput);
    if (isNaN(lowValue) || lowValue < 0.5 || lowValue > 10.0) {
      toast({
        title: "Validation Error",
        description: "Maximum adjustment must be between 0.5% and 10.0%.",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const isUpdate = !!config;
      const url = isUpdate
        ? `${API_URL}/api/admin/payment-adjustment-config/${programYear}`
        : `${API_URL}/api/admin/payment-adjustment-config`;
      const response = await fetch(url, {
        method: isUpdate ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          performance_year: programYear,
          low_scenario_input: lowValue,
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to save configuration');
      }
      const saved: PaymentAdjustmentConfigData = await response.json();
      const next = saved && saved.performance_year ? saved : {
        ...(config || {} as PaymentAdjustmentConfigData),
        performance_year: programYear,
        payment_year: paymentYear,
        low_scenario_input: lowValue,
      };
      setConfig(next);
      // Keep the prefetch cache in sync so a tab revisit shows this save.
      paymentAdjustmentCache.configs = [
        ...paymentAdjustmentCache.configs.filter(
          (c: PaymentAdjustmentConfigData) => c.performance_year !== programYear
        ),
        next,
      ];
      toast({
        title: isUpdate ? "Configuration Updated" : "Configuration Saved",
        description: `Payment adjustment for ${programYear} saved.`,
      });
    } catch (error) {
      console.error('Error saving payment adjustment config:', error);
      toast({
        title: "Save Failed",
        description: error instanceof Error ? error.message : "Could not save configuration.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <TooltipProvider>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Payment Adjustment
            <Badge variant="secondary" className="ml-1">{programYear}</Badge>
          </CardTitle>
          <CardDescription>
            The maximum bonus assumption used to project payment impact for the {programYear} performance year
            (paid in {paymentYear}). Switch the program year in the top bar to configure another year.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!isAdmin ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>Only administrators can configure payment adjustment settings.</AlertDescription>
            </Alert>
          ) : loading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4 items-start">
                <div className="space-y-2">
                  <Label htmlFor="low-scenario" className="flex items-center gap-1 text-sm">
                    Max bonus @ score 100 (low)
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3.5 w-3.5 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>Conservative estimate of the maximum bonus % at a perfect score. Medium and High scenarios are auto-derived (×1.25 and ×1.40).</p>
                      </TooltipContent>
                    </Tooltip>
                  </Label>
                  <div className="relative">
                    <Input
                      id="low-scenario"
                      type="number"
                      step="0.1"
                      min="0.5"
                      max="10.0"
                      value={lowScenarioInput}
                      onChange={(e) => setLowScenarioInput(e.target.value)}
                      disabled={saving}
                      className="pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Valid range: 0.5% – 10.0%</p>
                </div>

                {validInput && (
                  <div className="rounded-lg border bg-muted/30 p-3">
                    <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-2">
                      <Calculator className="h-3.5 w-3.5" />
                      Scenario maximums
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div>
                        <p className="text-muted-foreground text-xs">Low</p>
                        <p className="font-medium">{lowInput.toFixed(2)}%</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Medium ×1.25</p>
                        <p className="font-medium">{mediumCalc.toFixed(2)}%</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">High ×1.40</p>
                        <p className="font-medium">{highCalc.toFixed(2)}%</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3">
                <Button onClick={handleSave} disabled={!validInput || saving}>
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {config ? 'Update' : 'Save'}
                </Button>
                {config ? (
                  <span className="text-xs text-muted-foreground">Configured for {programYear}</span>
                ) : (
                  <span className="text-xs text-muted-foreground">Not yet configured for {programYear}</span>
                )}
              </div>

              {/* Collapsible help */}
              <button
                type="button"
                onClick={() => setHelpOpen(o => !o)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              >
                {helpOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                How payment adjustments work
              </button>
              {helpOpen && (
                <div className="rounded-lg bg-muted p-4">
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• <strong>Performance Year:</strong> the year MIPS performance is measured ({programYear}).</li>
                    <li>• <strong>Payment Year:</strong> when adjustments apply, always 2 years later ({paymentYear}).</li>
                    <li>• <strong>Three Scenarios:</strong> Low (conservative), Medium (likely), High (optimistic).</li>
                    <li>• <strong>Scaling Factor:</strong> {SCALING_FACTOR} converts the modeled max to the actual max adjustment.</li>
                    <li>• <strong>Score Zones:</strong> 0–18 = max penalty, 19–74 = graduated penalty, 75 = neutral, 76–100 = bonus.</li>
                  </ul>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}
